import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DocumentDuplicateIcon,
  BuildingLibraryIcon,
  ShieldCheckIcon,
  SwatchIcon,
  CheckIcon,
  CheckCircleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'es', label: 'Español' },
];

const NAV_LINKS = [
  { href: '#features', key: 'features' },
  { href: '#compliance', key: 'compliance' },
  { href: '#pricing', key: 'pricing' },
] as const;

const VALUE_ITEMS = [
  { key: 'item1', icon: DocumentDuplicateIcon },
  { key: 'item2', icon: BuildingLibraryIcon },
  { key: 'item3', icon: ShieldCheckIcon },
] as const;

const FEATURE_BLOCKS = [
  { key: 'quotesInvoices', id: 'features' },
  { key: 'bankReconciliation', id: 'compliance' },
  { key: 'customization', id: 'pricing' },
] as const;

// Mini product mockups per feature — reuse the app's card/badge styles instead of a bare icon
function FeatureVisual({ featureKey }: { featureKey: (typeof FEATURE_BLOCKS)[number]['key'] }) {
  if (featureKey === 'quotesInvoices') {
    return (
      <div className="card p-5 w-full">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
              <DocumentDuplicateIcon className="w-4 h-4 text-primary-600" />
            </div>
            <div>
              <div className="h-2.5 w-24 rounded bg-gray-300 mb-1.5" />
              <div className="h-2 w-16 rounded bg-gray-200" />
            </div>
          </div>
          <span className="badge badge-success">Payée</span>
        </div>
        <div className="space-y-2 mb-4">
          {[70, 55, 40].map((w) => (
            <div key={w} className="flex items-center justify-between">
              <div className="h-2 rounded bg-gray-200" style={{ width: `${w}%` }} />
              <div className="h-2 w-10 rounded bg-gray-200" />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-gray-100 pt-3">
          <div className="h-2.5 w-14 rounded bg-gray-200" />
          <div className="h-5 w-20 rounded-md bg-primary-100" />
        </div>
      </div>
    );
  }

  if (featureKey === 'bankReconciliation') {
    return (
      <div className="card p-5 w-full">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center shrink-0">
            <BuildingLibraryIcon className="w-4 h-4 text-accent-600" />
          </div>
          <div className="h-2.5 w-32 rounded bg-gray-300" />
        </div>
        <div className="space-y-2">
          {[0, 1, 2].map((row) => (
            <div key={row} className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2.5">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="w-4 h-4 text-accent-500 shrink-0" />
                <div className="h-2 w-28 rounded bg-gray-200" />
              </div>
              <div className="h-2 w-12 rounded bg-gray-200" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card p-5 w-full">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
          <SwatchIcon className="w-4 h-4 text-primary-600" />
        </div>
        <div className="h-2.5 w-28 rounded bg-gray-300" />
      </div>
      <div className="flex items-center gap-2 mb-4">
        {['bg-primary-600', 'bg-accent-500', 'bg-gray-800', 'bg-gray-300'].map((c) => (
          <span key={c} className={`w-6 h-6 rounded-full ring-2 ring-white shadow-sm ${c}`} />
        ))}
      </div>
      <div className="rounded-lg border border-gray-100 p-3">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 rounded bg-primary-200" />
          <div className="h-2 w-20 rounded bg-gray-200" />
        </div>
        <div className="h-1.5 rounded-full bg-primary-600 w-full mb-1.5" />
        <div className="h-1.5 rounded-full bg-gray-100 w-2/3" />
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [, setLanguageUpdate] = useState(0);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleLanguageChanged = () => setLanguageUpdate((prev) => prev + 1);
    i18n.on('languageChanged', handleLanguageChanged);
    return () => i18n.off('languageChanged', handleLanguageChanged);
  }, [i18n]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('ledgora-lang', lang);
    setLangMenuOpen(false);
    navigate(`/${lang}`, { replace: true });
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-white">
      {/* Sticky nav */}
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/90 backdrop-blur">
        <nav className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2">
            <img src="/logo.svg" alt="Ledgora" className="w-8 h-8" />
            <span className="text-lg font-bold text-gray-900">{import.meta.env.VITE_APP_NAME}</span>
          </a>

          <div className="hidden md:flex items-center gap-6">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t(`landing.nav.${link.key}`)}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangMenuOpen((open) => !open)}
                className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                aria-haspopup="listbox"
                aria-expanded={langMenuOpen}
              >
                {i18n.language.toUpperCase()}
                <ChevronDownIcon className="w-3.5 h-3.5" />
              </button>
              {langMenuOpen && (
                <div className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        i18n.language === lang.code ? 'text-primary-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
              {t('landing.nav.login')}
            </Link>
            <Link to="/register" className="btn btn-primary btn-sm">
              {t('landing.nav.cta')}
            </Link>
          </div>
        </nav>
      </header>

      <main id="top">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-14 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight leading-tight">
              {t('landing.hero.title')}
            </h1>
            <p className="mt-4 text-lg text-gray-600 max-w-xl">
              {t('landing.hero.subtitle')}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/register" className="btn btn-primary btn-lg">
                {t('landing.hero.cta')}
              </Link>
            </div>
            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500">
              <li className="flex items-center gap-1.5">
                <CheckIcon className="w-4 h-4 text-accent-600" />
                {t('landing.hero.trustNoEngagement')}
              </li>
              <li className="flex items-center gap-1.5">
                <CheckIcon className="w-4 h-4 text-accent-600" />
                {t('landing.hero.trustCompliant')}
              </li>
              <li className="flex items-center gap-1.5">
                <CheckIcon className="w-4 h-4 text-accent-600" />
                {t('landing.hero.trustSetup')}
              </li>
            </ul>
          </div>

          {/* Product visual — styled placeholder in the app's real card style */}
          <div className="card p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="h-4 w-28 rounded bg-gray-200" />
              <div className="h-7 w-20 rounded-lg bg-primary-50" />
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="card-body !p-3 bg-primary-50/60 border border-primary-100 rounded-lg">
                <div className="h-2 w-10 rounded bg-primary-200 mb-2" />
                <div className="h-4 w-14 rounded bg-primary-300" />
              </div>
              <div className="card-body !p-3 bg-accent-50/60 border border-accent-100 rounded-lg">
                <div className="h-2 w-10 rounded bg-accent-200 mb-2" />
                <div className="h-4 w-14 rounded bg-accent-300" />
              </div>
              <div className="card-body !p-3 bg-gray-50 border border-gray-100 rounded-lg">
                <div className="h-2 w-10 rounded bg-gray-200 mb-2" />
                <div className="h-4 w-14 rounded bg-gray-300" />
              </div>
            </div>
            <div className="space-y-2">
              {[0, 1, 2, 3].map((row) => (
                <div key={row} className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2">
                  <div className="h-2.5 w-24 rounded bg-gray-200" />
                  <div className="h-2.5 w-12 rounded bg-gray-200" />
                  <span className="badge badge-success">{t('common.status')}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Value strip */}
        <section className="border-y border-gray-100 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid sm:grid-cols-3 gap-6">
            {VALUE_ITEMS.map(({ key, icon: Icon }) => (
              <div key={key} className="flex items-center gap-3">
                <div className="shrink-0 w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{t(`landing.values.${key}.title`)}</p>
                  <p className="text-sm text-gray-500">{t(`landing.values.${key}.desc`)}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 space-y-16">
          {FEATURE_BLOCKS.map(({ key, id }, idx) => {
            const bullets = t(`landing.features.${key}.bullets`, { returnObjects: true }) as string[];
            const reversed = idx % 2 === 1;
            return (
              <div
                key={key}
                id={id}
                className={`grid lg:grid-cols-2 gap-10 items-center ${reversed ? 'lg:[&>*:first-child]:order-2' : ''}`}
              >
                <FeatureVisual featureKey={key} />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                    {t(`landing.features.${key}.title`)}
                  </h2>
                  <p className="mt-3 text-gray-600">{t(`landing.features.${key}.desc`)}</p>
                  <ul className="mt-4 space-y-2">
                    {bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckIcon className="w-4 h-4 text-accent-600 mt-0.5 shrink-0" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </section>

        {/* Compact stat line */}
        <section className="border-y border-gray-100 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 text-center">
            <p className="text-sm font-medium text-gray-500">{t('landing.stats')}</p>
          </div>
        </section>

        {/* Final CTA band */}
        <section className="bg-primary-600">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
              {t('landing.finalCta.title')}
            </h2>
            <div className="mt-6">
              <Link
                to="/register"
                className="btn btn-lg bg-white text-primary-700 hover:bg-primary-50 focus:ring-white"
              >
                {t('landing.finalCta.cta')}
              </Link>
            </div>
            <p className="mt-3 text-sm text-primary-100">{t('landing.finalCta.microcopy')}</p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.svg" alt="Ledgora" className="w-6 h-6" />
            <span className="text-sm font-semibold text-gray-900">{import.meta.env.VITE_APP_NAME}</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/terms" className="hover:text-gray-900 transition-colors">
              {t('landing.footer.privacy')}
            </Link>
            <a href="mailto:support@ledgora.be" className="hover:text-gray-900 transition-colors">
              {t('landing.footer.contact')}
            </a>
            <Link to="/login" className="hover:text-gray-900 transition-colors">
              {t('landing.footer.login')}
            </Link>
          </div>
          <p className="text-xs text-gray-400">
            {t('landing.footer.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
      </footer>
    </div>
  );
}
